import { useMemo, useRef, useState, useEffect } from 'react';
import { scaleLinear, scalePoint } from 'd3-scale';
import { area as d3area, line as d3line, curveMonotoneX } from 'd3-shape';
import { useThemeColors } from './useThemeColors.js';

/**
 * Area capacité (fond) vs ligne demande/allocated — SVG maison (d3-scale + d3-shape).
 *
 * data : [{ period, capacityHours, allocatedHours }]
 */
export default function LineAreaChart({ data = [], height = 320, formatLabel, unitSuffix = 'h' }) {
  const colors = useThemeColors();
  const ref = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(Math.max(280, Math.round(w)));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const margin = { top: 14, right: 16, bottom: 36, left: 46 };
  const innerW = Math.max(1, width - margin.left - margin.right);
  const innerH = Math.max(1, height - margin.top - margin.bottom);

  const { x, y, areaPath, linePath, ticks, maxLabels } = useMemo(() => {
    const xS = scalePoint()
      .domain(data.map((d) => d.period))
      .range([0, innerW])
      .padding(0.5);
    const maxV = Math.max(
      1,
      ...data.map((d) => Math.max(d.capacityHours || 0, d.allocatedHours || 0)),
    );
    const yS = scaleLinear().domain([0, maxV]).nice().range([innerH, 0]);
    const a = d3area()
      .x((d) => xS(d.period))
      .y0(innerH)
      .y1((d) => yS(d.capacityHours || 0))
      .curve(curveMonotoneX);
    const l = d3line()
      .x((d) => xS(d.period))
      .y((d) => yS(d.allocatedHours || 0))
      .curve(curveMonotoneX);
    // Limite le nombre d'étiquettes d'axe pour rester lisible.
    const step = Math.ceil(data.length / Math.max(1, Math.floor(innerW / 70)));
    return {
      x: xS,
      y: yS,
      areaPath: a(data) || '',
      linePath: l(data) || '',
      ticks: yS.ticks(4),
      maxLabels: step,
    };
  }, [data, innerW, innerH]);

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        role="img"
        data-testid="linearea-chart"
        style={{ display: 'block' }}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {ticks.map((t) => (
            <g key={t} transform={`translate(0,${y(t)})`}>
              <line x1={0} x2={innerW} stroke={colors.glassBorder} strokeDasharray="2 3" />
              <text x={-8} dy="0.32em" textAnchor="end" fontSize={10} fill={colors.textMuted}>
                {t}
              </text>
            </g>
          ))}
          <path data-testid="area-capacity" d={areaPath} fill={colors.accent} opacity={0.16} />
          <path
            data-testid="line-allocated"
            d={linePath}
            fill="none"
            stroke={colors.accent}
            strokeWidth={2}
          />
          {data.map((d, i) => (
            <circle
              key={d.period}
              data-testid={`point-${d.period}`}
              cx={x(d.period)}
              cy={y(d.allocatedHours || 0)}
              r={2.5}
              fill={colors.accent}
            >
              <title>{`${formatLabel ? formatLabel(d.period) : d.period} — Demande ${d.allocatedHours || 0}${unitSuffix} / Capacité ${d.capacityHours || 0}${unitSuffix}`}</title>
            </circle>
          ))}
          <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke={colors.glassBorder} />
          {data.map((d, i) =>
            i % maxLabels === 0 ? (
              <text
                key={d.period}
                x={x(d.period)}
                y={innerH + 14}
                textAnchor="middle"
                fontSize={9}
                fill={colors.textMuted}
              >
                {formatLabel ? formatLabel(d.period) : d.period}
              </text>
            ) : null,
          )}
        </g>
      </svg>
    </div>
  );
}
